import * as React from "react";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowRight,
  Check,
  ClipboardList,
  ShieldCheck,
  Users,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import LoadingSpinner from "@/components/LoadingSpinner";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { McpClientLogo } from "@/components/integrations/McpClientLogo";
import { trpc } from "@/lib/trpc/client";

const SCOPE_ICONS: Record<string, React.ReactNode> = {
  "clients.read": <Users className="h-4 w-4 text-muted-foreground" />,
  "checkins.read": <ClipboardList className="h-4 w-4 text-muted-foreground" />,
};

function getClientDomain(uri?: string | null): string {
  if (!uri) return "";
  try {
    return new URL(uri).hostname;
  } catch {
    return uri;
  }
}

export const Route = createFileRoute("/oauth/mcp/consent")({
  validateSearch: (search: Record<string, unknown>) => ({
    ticket: typeof search.ticket === "string" ? search.ticket : "",
  }),
  beforeLoad: async ({ context, location }) => {
    if (!context.session) {
      const next = `${location.pathname}${location.searchStr}`;
      throw redirect({
        href: `/login?next=${encodeURIComponent(next)}`,
        replace: true,
      });
    }
  },
  component: McpConsentPage,
});

function McpConsentPage() {
  const { ticket } = Route.useSearch();
  const preview = trpc.integrations.previewMcpConsent.useQuery(
    { ticket },
    { enabled: ticket.length > 0, retry: false },
  );
  const createGrant = trpc.integrations.createMcpGrant.useMutation();
  const [orgId, setOrgId] = React.useState<string>("");
  const [scopes, setScopes] = React.useState<string[]>([]);
  const [decision, setDecision] = React.useState<"approve" | "deny" | null>(
    null,
  );

  React.useEffect(() => {
    const previous = document.querySelector('meta[name="referrer"]');
    const meta =
      previous instanceof HTMLMetaElement
        ? previous
        : document.createElement("meta");
    const created = !(previous instanceof HTMLMetaElement);
    const previousContent = created ? null : meta.content;
    meta.name = "referrer";
    meta.content = "no-referrer";
    if (created) {
      document.head.appendChild(meta);
    }
    return () => {
      if (created) {
        meta.remove();
        return;
      }
      if (previousContent !== null) {
        meta.content = previousContent;
      }
    };
  }, []);

  React.useEffect(() => {
    if (!preview.data) return;
    const firstOrg =
      preview.data.orgs.find((org) => org.grantableScopes.length > 0) ??
      preview.data.orgs[0];
    if (firstOrg && !orgId) {
      setOrgId(firstOrg.orgId);
    }
  }, [preview.data, orgId]);

  const selectedOrg = preview.data?.orgs.find((org) => org.orgId === orgId);
  const grantable = selectedOrg?.grantableScopes ?? [];

  React.useEffect(() => {
    if (!preview.data || grantable.length === 0) {
      setScopes([]);
      return;
    }
    const grantableSet = new Set<string>(grantable);
    const requested = preview.data.requestedScopes.filter((scope) =>
      grantableSet.has(scope),
    );
    setScopes(requested.length > 0 ? requested : [...grantable]);
  }, [preview.data, grantable.join("|")]);

  const handleApprove = async () => {
    if (!orgId || scopes.length === 0) return;
    setDecision("approve");
    try {
      const result = await createGrant.mutateAsync({
        ticket,
        orgId,
        scopes,
      });
      window.location.assign(result.callbackUrl);
    } catch (error) {
      setDecision(null);
      const message =
        error instanceof Error ? error.message : "Failed to authorize";
      toast.error("Failed to authorize", { description: message });
    }
  };

  const handleDeny = () => {
    if (!preview.data?.denyUrl) return;
    setDecision("deny");
    window.location.assign(preview.data.denyUrl);
  };

  const loading = preview.isLoading;
  const errorMessage =
    !ticket
      ? "Authorization request missing or invalid."
      : preview.error?.message ?? null;

  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-background p-6 md:p-10">
      <div className="flex w-full max-w-md flex-col gap-8">
        {loading ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <LoadingSpinner className="h-5 w-5" />
            <p className="text-sm text-muted-foreground">
              Loading authorization details...
            </p>
          </div>
        ) : null}

        {!loading && errorMessage ? (
          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Unable to load authorization request</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
            <div className="flex justify-center">
              <Button asChild variant="outline">
                <Link to="/">Back to app</Link>
              </Button>
            </div>
          </div>
        ) : null}

        {!loading && !errorMessage && preview.data ? (
          <>
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="flex items-center gap-3">
                <McpClientLogo
                  name={preview.data.clientName}
                  uri={preview.data.clientUri}
                  className="h-14 w-14"
                />
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl border">
                  <img
                    src="/icon-512.png"
                    alt="Trainrr"
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <h1 className="text-xl font-semibold">
                  {preview.data.clientName} wants to access your Trainrr
                  organisation
                </h1>
                {getClientDomain(preview.data.clientUri) ? (
                  <p className="text-sm text-muted-foreground">
                    {getClientDomain(preview.data.clientUri)} is requesting
                    access
                  </p>
                ) : null}
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Organisation</p>
              {preview.data.orgs.length === 1 ? (
                <div className="rounded-xl border px-4 py-3 text-sm font-medium">
                  {preview.data.orgs[0]?.orgName}
                </div>
              ) : (
                <RadioGroup
                  value={orgId}
                  onValueChange={setOrgId}
                  className="divide-y rounded-xl border"
                >
                  {preview.data.orgs.map((org) => (
                    <label
                      key={org.orgId}
                      className="flex cursor-pointer items-center gap-3 px-4 py-3.5"
                    >
                      <RadioGroupItem value={org.orgId} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">{org.orgName}</p>
                        {org.roleKey ? (
                          <p className="text-xs text-muted-foreground">
                            {org.roleKey}
                          </p>
                        ) : null}
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              )}
            </div>

            <div className="space-y-1">
              <p className="mb-3 text-sm text-muted-foreground">
                Permissions
              </p>
              {grantable.length > 0 ? (
                <div className="divide-y rounded-xl border bg-background">
                  {grantable.map((scope) => {
                    const info = preview.data.scopeCatalog[scope];
                    const checked = scopes.includes(scope);
                    return (
                      <label
                        key={scope}
                        className="flex cursor-pointer items-center gap-3 px-4 py-3.5"
                      >
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                          {SCOPE_ICONS[scope] ?? (
                            <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">
                              {info?.label ?? scope}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {info?.description ?? "Access requested by this app"}
                          </p>
                        </div>
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(value) => {
                            setScopes((current) =>
                              value
                                ? [...current, scope]
                                : current.filter((item) => item !== scope),
                            );
                          }}
                        />
                      </label>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  You do not have permission to share data from this
                  organisation.
                </p>
              )}
            </div>

            <div className="flex items-center gap-3 rounded-xl bg-muted px-4 py-3">
              <ShieldCheck className="h-4 w-4 shrink-0 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">
                This app will not have access to your password. You can revoke
                access at any time from Settings → Connected apps.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="ghost"
                className="flex-1 text-base"
                onClick={handleDeny}
                disabled={decision !== null}
              >
                {decision === "deny" ? (
                  <LoadingSpinner className="h-4 w-4" />
                ) : (
                  <>
                    <XCircle className="h-4 w-4" />
                    Deny
                  </>
                )}
              </Button>
              <Button
                className="flex-1 text-base"
                onClick={() => void handleApprove()}
                disabled={
                  decision !== null || !orgId || scopes.length === 0
                }
              >
                {decision === "approve" ? (
                  <LoadingSpinner className="h-4 w-4" />
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    Authorize
                  </>
                )}
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
